import {Anchor, Box} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import RunAnalysisTemplateModal from 'analysis-tool/create-analysis-modal/RunAnalysisTemplateModal';
import BenchlingConfig from 'BenchlingConfig';
import DataCatalogItemDetails, {DataCatalogItemActionIcon} from 'data-catalog/DataCatalogItemDetails';
import DataCatalogOrigin from 'data-catalog/DataCatalogOrigin';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {updateFile} from 'files/network/FileDAO';
import {isSupportedMimeType} from 'files/preview/FilePreviewConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {fileStatusDisplay} from 'instruments/connection-files/ConnectionFilesStatus';
import notifications from 'notifications';
import {renderFilePreview} from 'pipelines/files/ScratchFilePaneContent';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import {readableTime} from 'util/DateUtil';
import * as modals from 'util/Modals';
import {formatBytes} from 'util/NameUtil';
import removeNulls from 'util/removeNulls';
import {callV3Internal} from 'v3-internal-client';

import {FileViewQuery} from './__generated/FileViewQuery';
import FileVersionDropdown from './FileVersionDropdown';

type File = ExtractByTypename<FileViewQuery['file'], 'FileNode'>;

interface FileDetailsProps {
  file: File;
  reload: () => void;
}

export default function FileDetails({file, reload}: FileDetailsProps): JSX.Element {
  // null follows the file's current version, including after a revert publishes a new one.
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isReverting, setIsReverting] = useState(false);
  const isVersionHistoryEnabled = BenchlingConfig.PIPELINES_ENABLE_FILE_VERSION_HISTORY;
  const currentVersionId = file.currentVersionId ?? null;
  const viewableVersions = isVersionHistoryEnabled
    ? file.versions.filter((version) => version.scratchFile != null)
    : [];
  const selectedVersion = viewableVersions.find(
    (version) => version.id === (selectedVersionId ?? currentVersionId)
  );
  const isViewingPastVersion = selectedVersion != null && selectedVersion.id !== currentVersionId;
  const scratchFile = selectedVersion?.scratchFile ?? file.scratchFile;

  const filePreview = renderFilePreview(scratchFile);
  const hasPreviewableContent = isSupportedMimeType(scratchFile.mimeType, scratchFile.filename);

  const viewLatestVersion = (): void => {
    setSelectedVersionId(null);
  };

  const revertToSelectedVersion = async (): Promise<void> => {
    if (selectedVersion == null || currentVersionId == null) {
      return;
    }
    const confirmed = await modals.confirm(
      `Revert to version ${selectedVersion.versionNumber}?`,
      `This publishes a new version of the file with the contents of version ${selectedVersion.versionNumber}. Earlier versions stay in the file's history.`,
      ButtonStyle.PRIMARY,
      {acceptText: 'Revert', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
    );
    if (!confirmed) {
      return;
    }
    setIsReverting(true);
    await callV3Internal('File.Revert', {
      pathParams: {file_id: file.id},
      body: {fileVersionId: selectedVersion.id, expectedVersionId: currentVersionId},
    })
      .then(
        () => {
          setSelectedVersionId(null);
          reload();
          notifications.success(`Reverted to version ${selectedVersion.versionNumber}.`);
        },
        (res: Response) => {
          notifications.notifyError(res);
        }
      )
      .finally(() => {
        setIsReverting(false);
      });
  };

  const extraColumns = [
    {label: 'Status', value: fileStatusDisplay(file, false)},
    {label: 'Benchling File ID', value: file.displayId},
    {label: 'Size', value: scratchFile.size ? formatBytes(scratchFile.size) : ''},
    {
      label: 'Origin',
      value: <DataCatalogOrigin originType={file.originType} lineageParent={file.lineageParent} />,
    },
    {label: 'Original File Name', value: scratchFile.filename},
    ...(selectedVersion != null && viewableVersions.length > 1
      ? [
          {
            label: 'Version',
            value: (
              <FileVersionDropdown
                currentVersionId={currentVersionId}
                onSelectVersion={setSelectedVersionId}
                selectedVersion={selectedVersion}
                versions={viewableVersions}
              />
            ),
          },
          {
            label: 'Version Published',
            value: selectedVersion.creator
              ? `${readableTime(selectedVersion.createdAt)} by ${selectedVersion.creator.name}`
              : readableTime(selectedVersion.createdAt),
          },
        ]
      : []),
  ];

  const actionIcons: Array<DataCatalogItemActionIcon | null> = [
    isAnalysisTemplatesEnabled() && BenchlingConfig.INSTRUMENTS_ENABLE
      ? {
          title: 'Run automation template',
          icon: 'analytics_analysis',
          onClick: () =>
            legacyRunModal(RunAnalysisTemplateModal, {
              variableDefaults: {FILE: file.id},
              initialAnalysisName: `Analyze ${file.name}`,
            }),
        }
      : null,
    isViewingPastVersion && file.permissions.writable
      ? {
          title: 'Revert to this version',
          icon: 'undo',
          disabled: isReverting,
          onClick: revertToSelectedVersion,
        }
      : null,
    {
      title: 'Download original file',
      icon: 'download',
      disabled: scratchFile.externalFileId == null || scratchFile.downloadUrl == null,
      onClick: async (): Promise<void> => {
        const signedURL = await performSignedFileQuery(
          scratchFile.externalFileId!,
          scratchFile.downloadUrl!,
          file.id
        );
        if (signedURL) {
          window.open(signedURL, '_blank', 'noopener noreferrer');
        }
      },
    },
  ];

  return (
    <DataCatalogItemDetails
      dataCatalogItem={file}
      extraColumns={extraColumns}
      metadataTitle="File Metadata"
      tagsEmptyMessage="This file has no custom fields."
      onChange={async (updateParams: {
        id: string;
        name?: string;
        folderId?: string;
        customFields?: {[name: string]: string};
      }): Promise<void> => {
        await updateFile(updateParams);
        reload();
      }}
      actionIcons={removeNulls(actionIcons)}
      hasContent={hasPreviewableContent}
      preferenceKey={`file:${scratchFile.mimeType ?? 'unknown'}`}
    >
      {isViewingPastVersion && (
        <Box pt="xs">
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            supportingInfo={
              <span>
                You&apos;re viewing a previous file version.{' '}
                <Anchor component="button" type="button" onClick={viewLatestVersion}>
                  View the latest version.
                </Anchor>
              </span>
            }
          />
        </Box>
      )}
      <div className={css(styles.filePreview)}>{filePreview}</div>
    </DataCatalogItemDetails>
  );
}

const styles = StyleSheet.create({
  filePreview: {
    paddingTop: 12,
    height: '80vh',
    minHeight: 400,
  },
});
